import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Shield,
  ArrowLeft,
  Printer,
  Download,
  CheckCircle,
  Clock,
  XCircle,
  MinusCircle,
  AlertTriangle,
  FileCheck,
  ChevronDown,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SspFullReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessmentId');
  
  const [loading, setLoading] = useState(true);
  const [sspData, setSSPData] = useState(null);
  const [detailedControls, setDetailedControls] = useState(null);
  const [nistControls, setNistControls] = useState(null);
  const [evidenceChecklist, setEvidenceChecklist] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchData();
  }, [assessmentId]);

  const fetchData = async () => {
    try {
      // Build query string for assessment-specific data
      const queryParam = assessmentId ? `?assessment_id=${assessmentId}` : '';
      
      const [dataRes, detailedRes, nistRes, evidenceRes] = await Promise.all([
        fetch(`${API_URL}/api/ssp-report/keystone${queryParam}`),
        fetch(`${API_URL}/api/ssp-report/keystone/detailed-controls${queryParam}`),
        fetch(`${API_URL}/api/ssp-report/keystone/nist-controls${queryParam}`),
        fetch(`${API_URL}/api/ssp-report/keystone/evidence-checklist${queryParam}`)
      ]);
      
      const detailedData = await detailedRes.json();
      
      setSSPData(await dataRes.json());
      setDetailedControls(detailedData);
      setNistControls(await nistRes.json());
      setEvidenceChecklist(await evidenceRes.json());
      
      // Expand all categories by default for print view
      const expanded = {};
      detailedData?.categories?.forEach(cat => {
        expanded[cat.name] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (name) => {
    setExpandedCategories(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const getAnswerStyle = (answer) => {
    switch (answer) {
      case 'Y': return { bg: 'bg-emerald-500', text: 'YES', border: 'border-emerald-200', rowBg: 'bg-emerald-50' };
      case 'N': return { bg: 'bg-red-500', text: 'NO', border: 'border-red-200', rowBg: 'bg-red-50' };
      case 'P': return { bg: 'bg-amber-500', text: 'PARTIAL', border: 'border-amber-200', rowBg: 'bg-amber-50' };
      case 'NA': return { bg: 'bg-slate-400', text: 'N/A', border: 'border-slate-200', rowBg: 'bg-slate-50' };
      default: return { bg: 'bg-slate-300', text: 'N/R', border: 'border-slate-200', rowBg: 'bg-slate-50' };
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  const stats = detailedControls?.summary || {};

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* Print-hidden navigation */}
      <div className="print:hidden sticky top-0 z-50 bg-slate-900 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/ssp-report')}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Full SSP Report</h1>
              <p className="text-slate-400 text-sm">Keystone Alliance - Print/Web View</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg font-medium transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print Report
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-5xl mx-auto px-8 py-12 print:px-0 print:py-0">
        
        {/* Cover Page */}
        <div className="text-center mb-16 print:mb-8 print:page-break-after">
          <div className="mb-8">
            <p className="text-slate-600 text-lg mb-2">Colt Cybersecurity Consultants | Aspiritech</p>
          </div>
          <h1 className="text-5xl font-bold text-slate-800 mb-4 print:text-4xl">System Security Plan</h1>
          <h2 className="text-3xl font-semibold text-violet-600 mb-2 print:text-2xl">Full Detailed Report</h2>
          <p className="text-2xl text-slate-600 mb-8 print:text-xl">{sspData?.organization?.name || 'Keystone Alliance'}</p>
          <p className="text-lg text-slate-500">{sspData?.organization?.system_name}</p>
          <p className="text-lg text-slate-500 print:hidden">Version {sspData?.organization?.version} - {sspData?.organization?.date}</p>
          <p className="text-lg text-slate-500 hidden print:block">Version {sspData?.organization?.version}</p>
          
          <div className="mt-12 p-6 bg-slate-50 rounded-xl inline-block print:bg-slate-100">
            <p className="text-slate-700 font-medium mb-4">This report includes:</p>
            <ul className="text-left text-slate-600 space-y-2">
              <li>• {detailedControls?.totalQuestions || 0} HIPAA Assessment Questions with Responses</li>
              <li>• MUST Statements for each control</li>
              <li>• Evidence Requirements checklist</li>
              <li>• {nistControls?.totalControls || 0} NIST SP 800-66 Controls</li>
              <li>• HIPAA to NIST Control Mapping</li>
            </ul>
          </div>
        </div>

        {/* Executive Summary */}
        <section className="mb-12 print:page-break-after">
          <h2 className="text-3xl font-bold text-slate-800 mb-6 border-b-2 border-violet-500 pb-2">
            1. Executive Summary
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">{sspData?.executive_summary?.overview}</p>
          <p className="text-slate-700">
            <span className="font-semibold">Compliance Framework:</span>{' '}
            <span className="text-violet-600 font-medium">{sspData?.executive_summary?.compliance_framework}</span>
          </p>
        </section>

        {/* Assessment Summary */}
        <section className="mb-12 print:page-break-after">
          <h2 className="text-3xl font-bold text-slate-800 mb-6 border-b-2 border-violet-500 pb-2">
            2. Assessment Summary
          </h2>
          <div className="grid grid-cols-5 gap-4 print:grid-cols-5">
            <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="text-3xl font-bold text-emerald-600">{stats.yes || 0}</div>
              <div className="text-sm text-emerald-700 font-medium">Yes</div>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="text-3xl font-bold text-amber-600">{stats.partial || 0}</div>
              <div className="text-sm text-amber-700 font-medium">Partial</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="text-3xl font-bold text-red-600">{stats.no || 0}</div>
              <div className="text-sm text-red-700 font-medium">No</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-3xl font-bold text-slate-600">{stats.na || 0}</div>
              <div className="text-sm text-slate-700 font-medium">N/A</div>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-3xl font-bold text-slate-500">{stats.noResponse || 0}</div>
              <div className="text-sm text-slate-600 font-medium">No Response</div>
            </div>
          </div>
        </section>

        {/* HIPAA Detailed Controls */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-800 mb-6 border-b-2 border-violet-500 pb-2">
            3. HIPAA Assessment - Detailed Controls
          </h2>

          {detailedControls?.categories?.map((category, catIdx) => (
            <div key={catIdx} className="mb-8 print:page-break-after">
              {/* Category Header */}
              <div 
                className="flex items-center justify-between p-4 bg-violet-50 rounded-lg mb-4 cursor-pointer print:cursor-default border border-violet-200"
                onClick={() => toggleCategory(category.name)}
              >
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-violet-600" />
                  <h3 className="text-xl font-bold text-violet-800">
                    3.{catIdx + 1} {category.name}
                  </h3>
                  <span className="text-violet-600">({category.controls.length} questions)</span>
                </div>
                <div className="flex items-center gap-4 print:hidden">
                  <span className="text-emerald-600 font-medium">{category.stats.yes} Yes</span>
                  <span className="text-amber-600 font-medium">{category.stats.partial} Partial</span>
                  <span className="text-red-600 font-medium">{category.stats.no} No</span>
                  <ChevronDown className={`w-5 h-5 text-violet-600 transition-transform ${expandedCategories[category.name] ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Questions */}
              <div className={`space-y-4 ${expandedCategories[category.name] ? '' : 'hidden print:block'}`}>
                {category.controls.map((control, ctrlIdx) => {
                  const style = getAnswerStyle(control.response?.answer);
                  return (
                    <div key={ctrlIdx} className="border border-slate-200 rounded-lg overflow-hidden print:break-inside-avoid">
                      {/* Question Row with Status Box */}
                      <div className={`flex ${style.rowBg}`}>
                        {/* Status Box */}
                        <div className={`${style.bg} text-white font-bold text-sm px-4 py-3 flex items-center justify-center min-w-[80px]`}>
                          {style.text}
                        </div>
                        {/* Question */}
                        <div className="flex-1 p-3">
                          <div className="flex items-start gap-2">
                            <span className="text-violet-600 font-mono text-sm font-bold">{control.id}</span>
                            <span className="text-slate-700">{control.text}</span>
                          </div>
                        </div>
                      </div>

                      {/* Response Details */}
                      <div className="p-4 bg-white border-t border-slate-100">
                        {control.response?.responseText && (
                          <div className="mb-3">
                            <span className="font-semibold text-slate-700">Response: </span>
                            <span className="text-slate-600 italic">{control.response.responseText}</span>
                          </div>
                        )}
                        {control.response?.comment && (
                          <div className="mb-3 text-sm text-slate-500">
                            <span className="font-medium">Note: </span>{control.response.comment}
                          </div>
                        )}

                        {/* Security Control Requirements - Show actual DOES/MUST status from user data */}
                        {control.mustStatements?.length > 0 && (
                          <div className="mb-3">
                            <div className="flex items-center gap-2 text-violet-600 font-semibold mb-2">
                              <AlertTriangle className="w-4 h-4" />
                              Security Control Requirements
                            </div>
                            <div className="space-y-2 ml-2">
                              {control.mustStatements.map((must, idx) => {
                                // Handle both string format (static data) and object format (user data)
                                let statementText = typeof must === 'string' ? must : must.statement;
                                const status = typeof must === 'object' ? must.status : 'MUST';
                                const isDoes = status === 'DOES';
                                
                                // Remove "MUST" prefix from statement text (redundant since badge shows status)
                                statementText = statementText.replace(/^MUST\s+/i, '');
                                
                                return (
                                  <div key={idx} className={`flex items-start gap-2 p-2 rounded border ${
                                    isDoes 
                                      ? 'bg-emerald-50 border-emerald-200' 
                                      : 'bg-red-50 border-red-200'
                                  }`}>
                                    <span className={`px-3 py-1 text-sm font-bold text-white rounded shadow-sm ${
                                      isDoes ? 'bg-emerald-600' : 'bg-red-600'
                                    }`}>
                                      {isDoes ? 'DOES' : 'MUST'}
                                    </span>
                                    <span className={`text-sm flex-1 pt-1 ${
                                      isDoes ? 'text-emerald-700' : 'text-slate-700'
                                    }`}>{statementText}</span>
                                  </div>
                                );
                              })}
                              {/* Fully Compliant Summary - based on actual compliance status */}
                              {(() => {
                                const allDoes = control.mustStatements.every(must => 
                                  typeof must === 'object' && must.status === 'DOES'
                                );
                                const isFullyCompliant = allDoes && control.response?.answer === 'Y';
                                
                                return (
                                  <div className={`flex items-start gap-2 mt-3 p-3 rounded-lg border-2 ${
                                    isFullyCompliant 
                                      ? 'bg-emerald-100 border-emerald-400' 
                                      : 'bg-red-100 border-red-400'
                                  }`}>
                                    <span className={`px-3 py-1 text-sm font-bold text-white rounded shadow-sm ${
                                      isFullyCompliant ? 'bg-emerald-600' : 'bg-red-600'
                                    }`}>
                                      {isFullyCompliant ? 'YES' : 'NO'}
                                    </span>
                                    <span className={`text-sm font-bold pt-1 ${
                                      isFullyCompliant ? 'text-emerald-700' : 'text-red-700'
                                    }`}>
                                      This security control is fully compliant
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Evidence Required */}
                        {control.evidenceRequired?.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 text-blue-600 font-semibold mb-2">
                              <FileCheck className="w-4 h-4" />
                              Evidence Required
                            </div>
                            <ul className="grid grid-cols-2 gap-1 ml-6 print:grid-cols-1">
                              {control.evidenceRequired.map((evidence, idx) => (
                                <li key={idx} className="text-slate-600 text-sm flex items-start gap-2">
                                  <span className="text-blue-500">☐</span> {evidence}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {/* NIST 800-66 Controls */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-800 mb-6 border-b-2 border-blue-500 pb-2">
            4. NIST SP 800-66 Controls
          </h2>
          <p className="text-slate-600 mb-6">{nistControls?.totalControls || 0} controls mapped to HIPAA Security Rule requirements</p>

          {nistControls?.families?.map((family, famIdx) => (
            <div key={famIdx} className="mb-8 print:page-break-after">
              <div className="p-4 bg-blue-50 rounded-lg mb-4 border border-blue-200">
                <h3 className="text-xl font-bold text-blue-800">
                  4.{famIdx + 1} {family.name}
                  <span className="text-blue-600 font-normal ml-2">({family.count} controls)</span>
                </h3>
              </div>

              <div className="space-y-4">
                {family.controls.map((control, ctrlIdx) => (
                  <div key={ctrlIdx} className="border border-slate-200 rounded-lg p-4 print:break-inside-avoid">
                    <div className="flex items-start gap-3 mb-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 font-mono text-sm rounded font-bold">
                        {control.controlId}
                      </span>
                      <span className="font-semibold text-slate-800">{control.title}</span>
                    </div>
                    <p className="text-slate-600 mb-3 ml-16">{control.description}</p>

                    {control.mustStatements?.length > 0 && (
                      <div className="ml-16 mb-3">
                        <div className="text-violet-600 font-semibold mb-1">Security Control Requirements:</div>
                        <ul className="space-y-1">
                          {control.mustStatements.map((must, idx) => {
                            // Handle both string format (static data) and object format (user data)
                            let statementText = typeof must === 'string' ? must : must.statement;
                            const status = typeof must === 'object' ? must.status : 'MUST';
                            const isDoes = status === 'DOES';
                            
                            // Remove "MUST" prefix from statement text (redundant since badge shows status)
                            statementText = statementText.replace(/^MUST\s+/i, '');
                            
                            return (
                              <li key={idx} className={`text-sm ${isDoes ? 'text-emerald-700' : 'text-slate-700'}`}>
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold text-white mr-2 ${
                                  isDoes ? 'bg-emerald-600' : 'bg-red-600'
                                }`}>
                                  {isDoes ? 'DOES' : 'MUST'}
                                </span>
                                {statementText}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {control.hipaaMapping?.length > 0 && (
                      <div className="ml-16">
                        <span className="text-violet-600 text-sm font-medium">
                          HIPAA Mapping: {control.hipaaMapping.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Evidence Checklist */}
        <section className="mb-12 print:page-break-before">
          <h2 className="text-3xl font-bold text-slate-800 mb-6 border-b-2 border-emerald-500 pb-2">
            5. Evidence Checklist
          </h2>
          
          {/* Per-Question Evidence from Assessment */}
          {evidenceChecklist?.perQuestionEvidence?.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-emerald-700 mb-4">Assessment Evidence Progress</h3>
              <div className="grid grid-cols-4 gap-4 mb-6 print:grid-cols-4">
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-emerald-600">{evidenceChecklist?.perQuestionSummary?.totalQuestions || 0}</div>
                  <p className="text-slate-600 text-sm">Questions</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-emerald-600">{evidenceChecklist?.perQuestionSummary?.totalItems || 0}</div>
                  <p className="text-slate-600 text-sm">Evidence Items</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-emerald-600">{evidenceChecklist?.perQuestionSummary?.checkedItems || 0}</div>
                  <p className="text-slate-600 text-sm">Collected</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <div className="text-2xl font-bold text-emerald-600">{evidenceChecklist?.perQuestionSummary?.completionRate || 0}%</div>
                  <p className="text-slate-600 text-sm">Complete</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {evidenceChecklist.perQuestionEvidence.map((question) => (
                  <div key={question.questionId} className="p-4 bg-slate-50 rounded-lg print:break-inside-avoid">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-emerald-600 font-mono text-sm font-bold">{question.questionId}</span>
                      <span className={`text-sm px-2 py-1 rounded ${
                        question.checkedItems === question.totalItems 
                          ? 'bg-emerald-100 text-emerald-600' 
                          : 'bg-amber-100 text-amber-600'
                      }`}>
                        {question.checkedItems}/{question.totalItems} collected
                      </span>
                    </div>
                    <p className="text-slate-700 text-sm mb-2">{question.questionText}</p>
                    <div className="space-y-1 ml-4">
                      {question.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <span className={item.checked ? 'text-emerald-500' : 'text-slate-400'}>
                            {item.checked ? '☑' : '☐'}
                          </span>
                          <span className={item.checked ? 'text-slate-700' : 'text-slate-500'}>
                            {item.text}
                          </span>
                          {item.isFromExcel && (
                            <span className="text-xs px-1 py-0.5 bg-cyan-100 text-cyan-600 rounded">Required</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <p className="text-slate-600 mb-6">{evidenceChecklist?.totalItems || 0} total evidence items required for compliance</p>

          <div className="space-y-6">
            {Object.entries(evidenceChecklist?.categories || {}).map(([category, items]) => {
              if (items.length === 0) return null;
              return (
                <div key={category} className="print:break-inside-avoid">
                  <h3 className="text-lg font-bold text-emerald-700 mb-3">
                    {category} <span className="text-emerald-600 font-normal">({items.length} items)</span>
                  </h3>
                  <div className="grid grid-cols-2 gap-2 print:grid-cols-1">
                    {items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-slate-700">
                        <span className="text-emerald-500">☐</span>
                        <span className="text-sm">{item.item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center pt-8 border-t border-slate-200 print:mt-8">
          <p className="text-slate-500">Colt Cybersecurity Consultants | Aspiritech</p>
          <p className="text-slate-400 text-sm print:hidden">
            Keystone Alliance SSP - Full Report v{sspData?.organization?.version || '1.0'}
          </p>
          <p className="text-slate-400 text-sm hidden print:block">
            Keystone Alliance System Security Plan - Full Report
          </p>
        </footer>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:page-break-after { page-break-after: always; }
          .print\\:page-break-before { page-break-before: always; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default SspFullReport;
